import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { 
  ArrowLeft, ArrowRight, Plus, X, Save, 
  Check, Building2, Map, Grid3X3, AlertCircle
} from 'lucide-react'
import { createRanch, createPaddock } from '../../services/api'

// Only owners can create ranches
const ALLOWED_ROLES = ['owner']

export default function RanchSetup({ currentUser }) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  
  const [step, setStep] = useState(1) // 1: Ranch Info, 2: Paddocks
  const [createdRanchId, setCreatedRanchId] = useState(null)
  const [error, setError] = useState('')

  // Ranch form data
  const [ranchData, setRanchData] = useState({
    name: '',
    code: '',
    timezone: 'Africa/Kampala',
    currency: 'UGX',
    country: 'Uganda',
    region: '',
  })
  
  const [ranchCoordinates, setRanchCoordinates] = useState([
    { lat: '', lng: '' },
    { lat: '', lng: '' },
    { lat: '', lng: '' },
  ])
  
  const [calculatedRanchArea, setCalculatedRanchArea] = useState(null)
  const [confirmedRanchArea, setConfirmedRanchArea] = useState('')

  // Paddocks data
  const [paddocks, setPaddocks] = useState([])
  const [currentPaddock, setCurrentPaddock] = useState({
    name: '',
    management_type: 'mmg',
    has_kraal: false,
    has_water: true,
    coordinates: [
      { lat: '', lng: '' },
      { lat: '', lng: '' },
      { lat: '', lng: '' },
    ]
  })

  // Check if user is allowed (owner only)
  const userRole = currentUser?.role_at_ranch || 'viewer'
  const isAllowed = ALLOWED_ROLES.includes(userRole)

  // Calculate area from coordinates using Shoelace formula
  const calculatePolygonArea = (coords) => {
    const validCoords = coords.filter(c => c.lat && c.lng)
    if (validCoords.length < 3) return null

    const points = validCoords.map(c => ({
      lat: parseFloat(c.lat),
      lng: parseFloat(c.lng)
    }))

    const n = points.length
    let area = 0
    for (let i = 0; i < n; i++) {
      const j = (i + 1) % n
      area += points[i].lng * points[j].lat
      area -= points[j].lng * points[i].lat
    }
    area = Math.abs(area) / 2

    // Convert from degrees² to approximate m² then to hectares
    // At Uganda's latitude (~0°), 1 degree ≈ 111,320 meters
    const metersPerDegree = 111320
    const areaM2 = area * metersPerDegree * metersPerDegree
    const areaHa = areaM2 / 10000

    return areaHa
  }

  // Check if point is inside polygon (ray casting algorithm)
  const isPointInPolygon = (point, polygon) => {
    const x = parseFloat(point.lat)
    const y = parseFloat(point.lng)
    
    let inside = false
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
      const xi = parseFloat(polygon[i].lat)
      const yi = parseFloat(polygon[i].lng)
      const xj = parseFloat(polygon[j].lat)
      const yj = parseFloat(polygon[j].lng)
      
      if (((yi > y) !== (yj > y)) && (x < (xj - xi) * (y - yi) / (yj - yi) + xi)) {
        inside = !inside
      }
    }
    return inside
  }

  // Check if all paddock coordinates are within ranch boundary
  const validatePaddockInRanch = (paddockCoords) => {
    const validRanchCoords = ranchCoordinates.filter(c => c.lat && c.lng)
    if (validRanchCoords.length < 3) return { valid: true, message: '' }

    const validPaddockCoords = paddockCoords.filter(c => c.lat && c.lng)
    for (const point of validPaddockCoords) {
      if (!isPointInPolygon(point, validRanchCoords)) {
        return { 
          valid: false, 
          message: `Point (${point.lat}, ${point.lng}) is outside ranch boundary` 
        }
      }
    }
    return { valid: true, message: '' }
  }

  // Check if two polygons overlap (simplified check)
  const doPolygonsOverlap = (poly1, poly2) => {
    // Check if any point of poly1 is inside poly2 or vice versa
    for (const point of poly1.filter(c => c.lat && c.lng)) {
      if (isPointInPolygon(point, poly2.filter(c => c.lat && c.lng))) {
        return true
      }
    }
    for (const point of poly2.filter(c => c.lat && c.lng)) {
      if (isPointInPolygon(point, poly1.filter(c => c.lat && c.lng))) {
        return true
      }
    }
    return false
  }

  // Calculate ranch area when coordinates change
  useEffect(() => {
    const area = calculatePolygonArea(ranchCoordinates)
    setCalculatedRanchArea(area)
    if (area && !confirmedRanchArea) {
      setConfirmedRanchArea(area.toFixed(2))
    }
  }, [ranchCoordinates])

  // Create ranch mutation
  const createRanchMutation = useMutation({
    mutationFn: createRanch,
    onSuccess: (data) => {
      setCreatedRanchId(data.id)
      queryClient.invalidateQueries(['ranches'])
      setStep(2) // Move to paddock setup
    },
    onError: (err) => setError(err.response?.data?.detail || 'Failed to create ranch'),
  })

  // Create paddock mutation
  const createPaddockMutation = useMutation({
    mutationFn: createPaddock,
    onSuccess: () => {
      queryClient.invalidateQueries(['paddocks'])
    },
    onError: (err) => setError(err.response?.data?.detail || 'Failed to create paddock'),
  })

  // Generate ranch code from name
  const generateCode = (name) => {
    return name
      .toUpperCase()
      .replace(/[^A-Z0-9]/g, '')
      .slice(0, 10) || 'RANCH'
  }

  // Handle ranch name change - auto-generate code
  const handleRanchNameChange = (name) => {
    setRanchData(prev => ({
      ...prev,
      name,
      code: prev.code || generateCode(name)
    }))
  }

  // Add coordinate point
  const addRanchCoordinate = () => {
    setRanchCoordinates([...ranchCoordinates, { lat: '', lng: '' }])
  }

  const removeRanchCoordinate = (index) => {
    if (ranchCoordinates.length > 3) {
      setRanchCoordinates(ranchCoordinates.filter((_, i) => i !== index))
    }
  }

  const updateRanchCoordinate = (index, field, value) => {
    const newCoords = [...ranchCoordinates]
    newCoords[index][field] = value
    setRanchCoordinates(newCoords)
  }

  // Paddock coordinate handlers
  const addPaddockCoordinate = () => {
    setCurrentPaddock(prev => ({
      ...prev,
      coordinates: [...prev.coordinates, { lat: '', lng: '' }]
    }))
  }

  const removePaddockCoordinate = (index) => {
    if (currentPaddock.coordinates.length > 3) {
      setCurrentPaddock(prev => ({
        ...prev,
        coordinates: prev.coordinates.filter((_, i) => i !== index)
      }))
    }
  }

  const updatePaddockCoordinate = (index, field, value) => {
    setCurrentPaddock(prev => {
      const newCoords = [...prev.coordinates]
      newCoords[index][field] = value
      return { ...prev, coordinates: newCoords }
    })
  }

  // Submit ranch
  const handleSubmitRanch = (e) => {
    e.preventDefault()
    setError('')

    if (!ranchData.name.trim()) {
      setError('Please enter a ranch name')
      return
    }

    const validCoords = ranchCoordinates.filter(c => c.lat && c.lng)
    if (validCoords.length < 3) {
      setError('Please enter at least 3 boundary coordinates')
      return
    }

    const payload = {
      name: ranchData.name,
      code: ranchData.code || generateCode(ranchData.name),
      area_ha: confirmedRanchArea ? parseFloat(confirmedRanchArea) : calculatedRanchArea,
      timezone: ranchData.timezone,
      currency: ranchData.currency,
      country: ranchData.country,
      region: ranchData.region,
      boundary_coordinates: validCoords,
    }

    createRanchMutation.mutate(payload)
  }

  // Add paddock to list
  const handleAddPaddock = () => {
    setError('')

    if (!currentPaddock.name.trim()) {
      setError('Please enter a paddock name')
      return
    }

    const validCoords = currentPaddock.coordinates.filter(c => c.lat && c.lng)
    if (validCoords.length < 3) {
      setError('Please enter at least 3 boundary coordinates for the paddock')
      return
    }

    // Validate paddock is within ranch
    const ranchValidation = validatePaddockInRanch(validCoords)
    if (!ranchValidation.valid) {
      setError(`Paddock boundary error: ${ranchValidation.message}`)
      return
    }

    // Check for overlap with existing paddocks
    for (const existingPaddock of paddocks) {
      if (doPolygonsOverlap(validCoords, existingPaddock.coordinates)) {
        setError(`Paddock "${currentPaddock.name}" overlaps with "${existingPaddock.name}"`)
        return
      }
    }

    // Calculate paddock area
    const paddockArea = calculatePolygonArea(validCoords)
    const paddockCode = `P${String(paddocks.length + 1).padStart(2, '0')}`

    const newPaddock = {
      ...currentPaddock,
      code: paddockCode,
      area_ha: paddockArea?.toFixed(4),
      coordinates: validCoords,
    }

    setPaddocks([...paddocks, newPaddock])

    // Reset form for next paddock
    setCurrentPaddock({
      name: '',
      management_type: 'mmg',
      has_kraal: false,
      has_water: true,
      coordinates: [
        { lat: '', lng: '' },
        { lat: '', lng: '' },
        { lat: '', lng: '' },
      ]
    })
  }

  // Remove paddock from list
  const handleRemovePaddock = (index) => {
    setPaddocks(paddocks.filter((_, i) => i !== index))
  }

  // Save all paddocks and finish
  const handleFinishSetup = async () => {
    setError('')

    if (paddocks.length === 0) {
      setError('Please add at least one paddock')
      return
    }

    try {
      // Create all paddocks
      for (const paddock of paddocks) {
        await createPaddockMutation.mutateAsync({
          ranch_id: createdRanchId,
          code: paddock.code,
          name: paddock.name,
          area_ha: paddock.area_ha ? parseFloat(paddock.area_ha) : null,
          management_type: paddock.management_type,
          has_kraal: paddock.has_kraal,
          has_water: paddock.has_water,
          boundary_coordinates: paddock.coordinates,
        })
      }

      // Navigate to dashboard
      navigate('/dashboard')
    } catch (err) {
      setError('Failed to create paddocks. Please try again.')
    }
  }

  // Permission check
  if (!isAllowed) {
    return (
      <div className="max-w-2xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-red-900 mb-2">Access Denied</h2>
          <p className="text-red-700 mb-4">
            Only ranch owners can set up new ranches and paddocks.
          </p>
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Go Back
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-3xl mx-auto p-6">
      {/* Progress Steps */}
      <div className="mb-8">
        <div className="flex items-center justify-center gap-4">
          <div className={`flex items-center gap-2 ${step >= 1 ? 'text-green-600' : 'text-gray-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
              step > 1 ? 'bg-green-600 text-white' : step === 1 ? 'bg-green-100 text-green-600 border-2 border-green-600' : 'bg-gray-100'
            }`}>
              {step > 1 ? <Check className="h-5 w-5" /> : '1'}
            </div>
            <span className="font-medium">Ranch Setup</span>
          </div>
          
          <div className="w-16 h-0.5 bg-gray-200">
            <div className={`h-full ${step > 1 ? 'bg-green-600' : 'bg-gray-200'}`} />
          </div>
          
          <div className={`flex items-center gap-2 ${step >= 2 ? 'text-green-600' : 'text-gray-400'}`}>
            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
              step === 2 ? 'bg-green-100 text-green-600 border-2 border-green-600' : 'bg-gray-100'
            }`}>
              2
            </div>
            <span className="font-medium">Paddock Setup</span>
          </div>
        </div>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
          {error}
        </div>
      )}

      {/* ================================================================== */}
      {/* STEP 1: Ranch Information */}
      {/* ================================================================== */}
      {step === 1 && (
        <form onSubmit={handleSubmitRanch} className="space-y-6">
          <div className="flex items-center gap-4 mb-6">
            <Building2 className="h-8 w-8 text-green-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Set Up Your Ranch</h1>
              <p className="text-gray-600">Enter ranch details and boundary coordinates</p>
            </div>
          </div>

          {/* Ranch Name & Code */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Ranch Information</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ranch Name *
                </label>
                <input
                  type="text"
                  required
                  value={ranchData.name}
                  onChange={(e) => handleRanchNameChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  placeholder="e.g., Sunrise Valley Ranch"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ranch Code
                </label>
                <input
                  type="text"
                  value={ranchData.code}
                  onChange={(e) => setRanchData({ ...ranchData, code: e.target.value.toUpperCase() })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  placeholder="Auto-generated"
                  maxLength={10}
                />
                <p className="text-xs text-gray-500 mt-1">Auto-generated from name if left blank</p>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Region
                </label>
                <input
                  type="text"
                  value={ranchData.region}
                  onChange={(e) => setRanchData({ ...ranchData, region: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  placeholder="e.g., Central Region"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Country
                </label>
                <input
                  type="text"
                  value={ranchData.country}
                  disabled
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-500"
                />
              </div>
            </div>

            {/* Defaults info */}
            <div className="mt-4 p-3 bg-blue-50 rounded-lg text-sm text-blue-800">
              <strong>Defaults:</strong> Timezone: East Africa Time (EAT) • Currency: Uganda Shillings (UGX)
            </div>
          </div>

          {/* Ranch Boundary Coordinates */}
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <Map className="h-5 w-5 text-blue-600" />
                <h2 className="text-lg font-semibold text-gray-900">Ranch Boundary</h2>
              </div>
              <button
                type="button"
                onClick={addRanchCoordinate}
                className="flex items-center gap-1 px-3 py-1 text-sm bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200"
              >
                <Plus className="h-4 w-4" />
                Add Point
              </button>
            </div>

            <p className="text-sm text-gray-600 mb-4">
              Enter at least 3 GPS coordinate points to define the ranch boundary. 
              Walk or drive the perimeter and record coordinates at each corner.
            </p>

            <div className="space-y-3">
              {ranchCoordinates.map((coord, index) => (
                <div key={index} className="flex items-center gap-2">
                  <span className="text-sm text-gray-500 w-8">#{index + 1}</span>
                  <input
                    type="number"
                    step="any"
                    placeholder="Latitude (e.g., 0.3476)"
                    value={coord.lat}
                    onChange={(e) => updateRanchCoordinate(index, 'lat', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  />
                  <input
                    type="number"
                    step="any"
                    placeholder="Longitude (e.g., 32.5825)"
                    value={coord.lng}
                    onChange={(e) => updateRanchCoordinate(index, 'lng', e.target.value)}
                    className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  />
                  {ranchCoordinates.length > 3 && (
                    <button
                      type="button"
                      onClick={() => removeRanchCoordinate(index)}
                      className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {/* Calculated Area */}
            {calculatedRanchArea && (
              <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm text-green-700">
                      <strong>Calculated Area:</strong> {calculatedRanchArea.toFixed(2)} hectares
                    </p>
                    <p className="text-xs text-green-600 mt-1">
                      You can adjust this if needed
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      step="0.01"
                      value={confirmedRanchArea}
                      onChange={(e) => setConfirmedRanchArea(e.target.value)}
                      className="w-32 px-3 py-2 border border-green-300 rounded-lg focus:ring-2 focus:ring-green-500"
                    />
                    <span className="text-sm text-green-700">ha</span>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Submit */}
          <div className="flex justify-end gap-3">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="px-6 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createRanchMutation.isPending}
              className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              {createRanchMutation.isPending ? 'Creating...' : 'Continue to Paddocks'}
              <ArrowRight className="h-5 w-5" />
            </button>
          </div>
        </form>
      )}

      {/* ================================================================== */}
      {/* STEP 2: Paddock Setup */}
      {/* ================================================================== */}
      {step === 2 && (
        <div className="space-y-6">
          <div className="flex items-center gap-4 mb-6">
            <Grid3X3 className="h-8 w-8 text-green-600" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Set Up Paddocks</h1>
              <p className="text-gray-600">Define paddock boundaries within your ranch</p>
            </div>
          </div>

          {/* Success message */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center gap-2 text-green-800">
              <Check className="h-5 w-5" />
              <span><strong>Ranch created successfully!</strong> Now add your paddocks.</span>
            </div>
          </div>

          {/* Added Paddocks List */}
          {paddocks.length > 0 && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">
                Added Paddocks ({paddocks.length})
              </h2>
              <div className="space-y-2">
                {paddocks.map((paddock, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg"
                  >
                    <div>
                      <span className="font-medium">{paddock.code}</span>
                      <span className="mx-2 text-gray-400">•</span>
                      <span>{paddock.name}</span>
                      <span className="mx-2 text-gray-400">•</span>
                      <span className="text-sm text-gray-600">{paddock.area_ha} ha</span>
                      <span className="mx-2 text-gray-400">•</span>
                      <span className="text-sm text-gray-600 uppercase">{paddock.management_type}</span>
                    </div>
                    <button
                      onClick={() => handleRemovePaddock(index)}
                      className="p-1 text-red-500 hover:bg-red-50 rounded"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Add New Paddock Form */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              {paddocks.length === 0 ? 'Add First Paddock' : 'Add Another Paddock'}
            </h2>

            <div className="space-y-4">
              {/* Paddock Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Paddock Name *
                </label>
                <input
                  type="text"
                  value={currentPaddock.name}
                  onChange={(e) => setCurrentPaddock({ ...currentPaddock, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  placeholder="e.g., North Pasture"
                />
              </div>

              {/* Management Type & Features */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Management Type
                  </label>
                  <select
                    value={currentPaddock.management_type}
                    onChange={(e) => setCurrentPaddock({ ...currentPaddock, management_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                  >
                    <option value="mmg">MMG (Multi-paddock Mob Grazing)</option>
                    <option value="rotational">Rotational</option>
                    <option value="continuous">Continuous</option>
                    <option value="strip">Strip Grazing</option>
                    <option value="holding">Holding</option>
                  </select>
                </div>

                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={currentPaddock.has_kraal}
                      onChange={(e) => setCurrentPaddock({ ...currentPaddock, has_kraal: e.target.checked })}
                      className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                    />
                    <span className="text-sm text-gray-700">Has Kraal</span>
                  </label>
                </div>

                <div className="flex items-center gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={currentPaddock.has_water}
                      onChange={(e) => setCurrentPaddock({ ...currentPaddock, has_water: e.target.checked })}
                      className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                    />
                    <span className="text-sm text-gray-700">Has Water</span>
                  </label>
                </div>
              </div>

              {/* Paddock Coordinates */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">
                    Paddock Boundary Coordinates *
                  </label>
                  <button
                    type="button"
                    onClick={addPaddockCoordinate}
                    className="flex items-center gap-1 px-2 py-1 text-xs bg-blue-100 text-blue-700 rounded hover:bg-blue-200"
                  >
                    <Plus className="h-3 w-3" />
                    Add Point
                  </button>
                </div>

                <div className="space-y-2">
                  {currentPaddock.coordinates.map((coord, index) => (
                    <div key={index} className="flex items-center gap-2">
                      <span className="text-xs text-gray-500 w-6">#{index + 1}</span>
                      <input
                        type="number"
                        step="any"
                        placeholder="Latitude"
                        value={coord.lat}
                        onChange={(e) => updatePaddockCoordinate(index, 'lat', e.target.value)}
                        className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                      />
                      <input
                        type="number"
                        step="any"
                        placeholder="Longitude"
                        value={coord.lng}
                        onChange={(e) => updatePaddockCoordinate(index, 'lng', e.target.value)}
                        className="flex-1 px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500"
                      />
                      {currentPaddock.coordinates.length > 3 && (
                        <button
                          type="button"
                          onClick={() => removePaddockCoordinate(index)}
                          className="p-1 text-red-500 hover:bg-red-50 rounded"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  ))}
                </div>

                {/* Calculated paddock area preview */}
                {(() => {
                  const area = calculatePolygonArea(currentPaddock.coordinates)
                  if (area) {
                    return (
                      <p className="mt-2 text-sm text-green-600">
                        Calculated area: <strong>{area.toFixed(4)} ha</strong>
                      </p>
                    )
                  }
                  return null
                })()}
              </div>

              {/* Add Paddock Button */}
              <button
                type="button"
                onClick={handleAddPaddock}
                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200"
              >
                <Plus className="h-5 w-5" />
                Add Paddock
              </button>
            </div>
          </div>

          {/* Finish Setup */}
          <div className="flex justify-between gap-3">
            <button
              type="button"
              onClick={() => setStep(1)}
              className="flex items-center gap-2 px-6 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200"
            >
              <ArrowLeft className="h-5 w-5" />
              Back
            </button>
            <button
              onClick={handleFinishSetup}
              disabled={paddocks.length === 0 || createPaddockMutation.isPending}
              className="flex items-center gap-2 px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
            >
              <Save className="h-5 w-5" />
              {createPaddockMutation.isPending ? 'Saving...' : `Finish Setup (${paddocks.length} paddocks)`}
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
